<template>
  <Sidebar
    :visible.sync="visibleProxy"
    position="right"
    :showCloseIcon="false"
    :dismissable="true"
    :blockScroll="true"
    :baseZIndex="11050"
    :style="sidebarStyle"
    class="reservation-detail-sidebar"
    @show="onShow"
    @hide="onHide"
  >
    <div class="rsv-drawer">
      <!-- Barra superior del drawer -->
      <div class="rsv-drawer__topbar">
        <div class="rsv-drawer__title">
          <span class="rsv-drawer__dot"></span>
          <span>{{ $t('reservationDrawer.title') }}</span>
        </div>

        <div class="rsv-drawer__tools">
          <button
            type="button"
            class="rsv-drawer__icon-btn"
            :title="$t('reservationDrawer.openFullPage')"
            @click="openFullPage"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
            </svg>
          </button>
          <button
            type="button"
            class="rsv-drawer__icon-btn rsv-drawer__icon-btn--close"
            :title="$t('reservationDrawer.close')"
            @click="close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Cuerpo: reutiliza íntegramente la vista de reserva en modo embebido -->
      <div class="rsv-drawer__body">
        <PageReservation
          v-if="visible && reservaId"
          :key="reservaId + '-' + reloadToken"
          :id="String(userId)"
          :reserva="String(reservaId)"
          :embedded="true"
          :initial-tab="initialTab"
          :initial-chat-id="initialChatId"
        />
      </div>
    </div>
  </Sidebar>
</template>

<script>
import Sidebar from "primevue/sidebar";
import DomHandler from "primevue/utils/DomHandler";
import PageReservation from "@/views/Business/Reservas/PageReservation.vue";

// El drawer abre con un baseZIndex alto (11050) para tapar nav/sidebar/banner.
// PrimeVue calcula cada z como baseZIndex + contador-global, por lo que los
// modales hijos (editar reserva, confirmar, etc.) que abren con baseZIndex 0
// quedarían MUY por debajo. Este valor debe coincidir con :baseZIndex del
// Sidebar para recolocar el contador por encima del drawer al abrir.
const DRAWER_BASE_Z = 11050;

export default {
  name: "ReservationDrawer",
  components: { Sidebar, PageReservation },
  props: {
    visible: { type: Boolean, default: false },
    userId: { type: [String, Number], default: null },
    reservaId: { type: [String, Number], default: null },
    // Cambia (bump) para forzar la recarga de la vista embebida tras editar la
    // reserva desde el modal apilado, sin cerrar el drawer.
    reloadToken: { type: [String, Number], default: 0 },
    // Pestaña de conversación en la que abrir (whatsapp | web | otas) y, si se
    // conoce, la conversación concreta. Lo usa la bandeja de mensajes del nav para
    // aterrizar en el canal por el que escribió el huésped.
    initialTab: { type: String, default: null },
    initialChatId: { type: [String, Number], default: null },
  },
  computed: {
    visibleProxy: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    // El ancho del Sidebar (PrimeVue) DEBE fijarse inline: el elemento raíz
    // .p-sidebar se renderiza portaleado, así que ::v-deep(.p-sidebar) no lo
    // alcanza. ~90rem en escritorio; completo en pantallas pequeñas.
    sidebarStyle() {
      if (this.$isMobile) return { width: "100vw", maxWidth: "100vw" };
      return { width: "min(90rem, 96vw)", maxWidth: "96vw" };
    },
  },
  methods: {
    onShow() {
      // OJO: el Sidebar emite 'show' ANTES de fijar su propio z-index
      // (Sidebar.vue onEnter: emit('show') y luego baseZIndex + generateZIndex).
      // Por eso diferimos a nextTick: cuando el drawer ya tomó su z, subimos el
      // contador global por encima para que CUALQUIER overlay posterior (editar
      // reserva, ConfirmDialog, dropdowns…) quede por ENCIMA del drawer y no detrás.
      this.$nextTick(() => {
        DomHandler.zindex = DRAWER_BASE_Z + DomHandler.getCurrentZIndex() + 5;
      });
      // El modal de pago es de Bootstrap (z-index por CSS, ajeno al contador):
      // marcamos el body para elevarlo solo mientras el drawer está abierto.
      document.body.classList.add("rsv-drawer-open");
    },
    onHide() {
      // CLAVE: reseteamos el contador global de PrimeVue al cerrar. El Sidebar usa
      // z = baseZIndex(11050) + contador; como en onShow inflamos el contador para
      // tapar al drawer con sus hijos, si NO lo reseteáramos aquí el z del drawer
      // crecería en cada apertura (12050 → 23106 → …) hasta superar a sus propios
      // modales (editar reserva) y dejarlos por detrás. 999 es la base de PrimeVue.
      DomHandler.zindex = 999;
      document.body.classList.remove("rsv-drawer-open");
    },
    close() {
      this.visibleProxy = false;
    },
    openFullPage() {
      if (!this.userId || !this.reservaId) return;
      const routeData = this.$router.resolve({
        path:
          "/business/scheduled-checkins/" +
          this.userId +
          "/" +
          this.reservaId +
          "/booking",
      });
      window.open(routeData.href, "_blank");
    },
  },
};
</script>

<style scoped lang="scss">
.rsv-drawer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f7f8fb;
  font-family: "DM Sans", sans-serif;
  /* El Sidebar de PrimeVue se renderiza dentro de #app, que trae del scaffold de
     Vue `text-align: center` (App.scss). Sin esto, el contenido del drawer
     (PageReservation embebido) sale centrado. Lo devolvemos a la izquierda. */
  text-align: left;
}

/* ── Barra superior ─────────────────────────────────────────────── */
.rsv-drawer__topbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 52px;
  padding: 0 14px 0 20px;
  background: #ffffff;
  border-bottom: 1px solid #e9edf3;
}
.rsv-drawer__title {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13.5px;
  font-weight: 700;
  color: #0e1320;
  letter-spacing: -0.01em;
}
.rsv-drawer__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #11b14d;
  box-shadow: 0 0 0 3px rgba(17, 177, 77, 0.16);
}
.rsv-drawer__tools {
  display: flex;
  align-items: center;
  gap: 6px;
}
.rsv-drawer__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 1px solid #e7eaf0;
  background: #fff;
  color: #6b7388;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease, border-color 0.16s ease;
}
.rsv-drawer__icon-btn:hover {
  background: #f4f6fa;
  color: #0e1320;
  border-color: #dbe0e8;
}
.rsv-drawer__icon-btn--close:hover {
  background: #fef1f1;
  color: #e5484d;
  border-color: #f6cccc;
}

/* ── Cuerpo con scroll propio (la vista embebida vive aquí) ─────── */
.rsv-drawer__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 20px 26px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #dde3ec transparent;
}
.rsv-drawer__body::-webkit-scrollbar {
  width: 8px;
}
.rsv-drawer__body::-webkit-scrollbar-thumb {
  background: #dde3ec;
  border-radius: 99px;
}

/* Los overrides del PrimeVue Sidebar (header/content) viven en el <style>
   global de abajo: el Sidebar se portalea a <body> y ::v-deep no lo alcanza. */

/* ── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 700px) {
  .rsv-drawer__body {
    padding: 12px 12px 20px;
  }
}

/* ===== RTL (arabe): el contenido del drawer se alinea a la derecha =====
   Nota: el drawer sigue abriendo desde position="right"; si se quiere que
   abra desde la izquierda en arabe, es un cambio de prop, no de CSS. */
[dir="rtl"] .rsv-drawer { text-align: right; }
</style>

<!-- Global (no scoped): el Sidebar de PrimeVue se portalea a <body>, así que
     sus overrides van aquí. La clase .reservation-detail-sidebar es única, no
     afecta a otros sidebars. -->
<style>
/* Quita la cabecera vacía del Sidebar (no usamos su botón cerrar) y su hueco */
.reservation-detail-sidebar .p-sidebar-header {
  display: none !important;
}
/* Sin padding por defecto: el cuerpo (.rsv-drawer__body) maneja su propio espaciado */
.reservation-detail-sidebar .p-sidebar-content {
  padding: 0 !important;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* El modal de pago es Bootstrap (z por CSS, ajeno al contador de PrimeVue).
   Mientras el drawer está abierto lo elevamos por encima de él.
   La clase rsv-drawer-open la pone/quita onShow/onHide. */
body.rsv-drawer-open #stateChagePayment {
  z-index: 12200 !important;
}
body.rsv-drawer-open .modal-backdrop {
  z-index: 12190 !important;
}
/* Overlay del modal de código estático (z fijo 9999, propio): elevarlo. */
body.rsv-drawer-open .scm-overlay {
  z-index: 12300 !important;
}

/* CUALQUIER modal de Bootstrap (p. ej. #modalDatos "Información del registro",
   editar reserva, confirmaciones…) abierto con el drawer activo debe ir por
   ENCIMA del drawer (baseZIndex 11050) Y de su backdrop elevado (12190). Sin
   esto, el modal quedaba por DEBAJO de su propia sombra negra y no se veía. */
body.rsv-drawer-open .modal {
  z-index: 12200 !important;
}
</style>
